import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Download, Filter } from "lucide-react";
import { api, nzd, num } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Stat, Badge, Loading, CHART_GRID } from "@/components/kit";

export default function Forecast() {
  const { activeStore } = useAuth();
  const [f, setF] = useState(null);
  const [target, setTarget] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const load = () => {
    if (!activeStore) return;
    setF(null);
    api
      .get("/forecast", { params: { store_id: activeStore, target: target || undefined } })
      .then(({ data }) => setF(data));
  };
  useEffect(load, [activeStore, target]);

  if (!f) return <Loading label="Calculating product demand models…" />;

  const categories = ["All", ...new Set(f.products.map((p) => p.category))];
  const filteredProducts =
    selectedCategory === "All"
      ? f.products
      : f.products.filter((p) => p.category === selectedCategory);

  const chartData = filteredProducts.slice(0, 10).map((p) => ({
    name: p.name,
    qty: p.predicted_qty,
    baseline: p.baseline,
  }));

  const exportCSV = () => {
    const headers = "Product,Category,Baseline,Weather_Multiplier,Predicted_Qty,Predicted_Revenue\n";
    const rows = filteredProducts
      .map(
        (p) =>
          `"${p.name}","${p.category}",${p.baseline},${(p.temp_mult * p.rain_mult).toFixed(2)},${p.predicted_qty},${p.predicted_revenue}`
      )
      .join("\n");
    const blob = new Blob([headers + rows], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Optics_Forecast_${f.date}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Demand Forecast"
        subtitle={`${f.weekday} · ${f.date} · ${f.weather.temp}°C, ${Math.round(f.weather.rain_prob * 100)}% rain · Model Confidence: ${Math.round(f.confidence * 100)}%`}
        right={
          <div className="flex items-center gap-2.5">
            <button
              onClick={exportCSV}
              className="inline-flex items-center gap-1.5 border border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-700 px-3 py-2 rounded-xl text-xs font-semibold shadow-2xs transition-colors"
            >
              <Download className="h-3.5 w-3.5" /> Export CSV
            </button>
            <div className="relative">
              <input
                type="date"
                data-testid="forecast-date"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                className="border border-zinc-200 bg-white rounded-xl px-3 py-2 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-zinc-900 shadow-2xs"
              />
            </div>
          </div>
        }
      />

      {/* KPI Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat
          testid="fc-txn"
          label="Predicted Orders"
          value={num(f.predicted_transactions)}
          sub={`${f.vs_normal_pct >= 0 ? "+" : ""}${f.vs_normal_pct}% vs normal ${f.weekday}`}
          accent={f.vs_normal_pct >= 0 ? "text-emerald-600" : "text-orange-600"}
        />
        <Stat testid="fc-rev" label="Forecast Revenue" value={nzd(f.predicted_revenue)} />
        <Stat label="Model Confidence" value={`${Math.round(f.confidence * 100)}%`} accent="text-emerald-600" />
        <Stat
          label="Events & Holidays"
          value={f.events.length ? `${f.events.length} Active` : "None"}
          sub={f.events.map((e) => e.name).join(", ") || "Normal Schedule"}
        />
      </div>

      {/* Category Pills Filter */}
      <div className="flex items-center gap-2 overflow-x-auto py-1">
        <span className="text-xs font-bold uppercase tracking-wider text-zinc-400 mr-1 flex items-center gap-1">
          <Filter className="h-3 w-3" /> Category:
        </span>
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setSelectedCategory(cat)}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all duration-150 ${
              selectedCategory === cat
                ? "bg-zinc-900 text-white shadow-xs"
                : "bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Chart & Ingredient Requirements */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 lg:col-span-2" testid="fc-chart">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs font-bold uppercase tracking-wider text-zinc-400">
              Top Predicted Menu Demand ({selectedCategory})
            </div>
            <span className="text-[11px] text-zinc-400">Units predicted for prep</span>
          </div>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 20 }}>
              <CartesianGrid horizontal={false} stroke={CHART_GRID} />
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 12 }} />
              <Tooltip cursor={{ fill: "#f4f4f5" }} />
              <Bar dataKey="qty" fill="#18181b" radius={[0, 4, 4, 0]} barSize={16} name="Forecast" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6 flex flex-col justify-between" testid="fc-ingredients">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-4">
              Required Raw Ingredients
            </div>
            <div className="space-y-3">
              {f.ingredients.map((i) => (
                <div key={i.name} className="flex items-center justify-between border-b border-zinc-100 pb-2.5">
                  <div>
                    <div className="text-xs font-semibold text-zinc-800">{i.name}</div>
                    <div className="text-[11px] text-zinc-400">Recipe consumption</div>
                  </div>
                  <span className="font-display font-bold text-sm text-zinc-900 tnum">
                    {num(i.required, 1)} {i.unit}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-4 mt-6 border-t border-zinc-100 text-[11px] text-zinc-400">
            Automatically factored into your automated purchase orders.
          </div>
        </Card>
      </div>

      {/* Product Detail Table */}
      <Card className="overflow-hidden" testid="fc-table">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/50">
          <div className="text-xs font-bold uppercase tracking-wider text-zinc-700">
            Detailed Menu Item Forecasts ({filteredProducts.length} Items)
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left uppercase tracking-wider text-zinc-400 border-b border-zinc-200 bg-zinc-50/30">
                <th className="px-5 py-3 font-semibold">Product</th>
                <th className="px-5 py-3 font-semibold">Category</th>
                <th className="px-5 py-3 font-semibold text-right">Historical Baseline</th>
                <th className="px-5 py-3 font-semibold text-right">Weather Factor</th>
                <th className="px-5 py-3 font-semibold text-right">Forecast Demand</th>
                <th className="px-5 py-3 font-semibold text-right">Forecast Revenue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-50">
              {filteredProducts.map((p) => {
                const wf = p.temp_mult * p.rain_mult * p.event_mult;
                return (
                  <tr key={p.product_id} className="hover:bg-zinc-50/80 transition-colors">
                    <td className="px-5 py-3 font-semibold text-zinc-900">{p.name}</td>
                    <td className="px-5 py-3 text-zinc-500">{p.category}</td>
                    <td className="px-5 py-3 text-right tnum text-zinc-500">{num(p.baseline, 0)} units</td>
                    <td className="px-5 py-3 text-right tnum">
                      <Badge level={wf >= 1.05 ? "success" : wf <= 0.95 ? "warning" : "muted"}>
                        {wf >= 1 ? "+" : ""}
                        {Math.round((wf - 1) * 100)}%
                      </Badge>
                    </td>
                    <td className="px-5 py-3 text-right tnum font-bold text-zinc-900">{num(p.predicted_qty)}</td>
                    <td className="px-5 py-3 text-right tnum font-medium text-emerald-700">{nzd(p.predicted_revenue)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
